/** PICK YOUR WINNER — one tap on one of tonight's two teams.
 *
 *  Sits ABOVE the star pick because it is the simpler pick: no player list,
 *  just the two teams, each in its own colour. Changeable until the game
 *  starts, then final. The points are banked on the fan's Patron Points
 *  account, so a phone that is not signed in is told to sign in first.
 */
import { useState } from 'react';
import { Loader2, Trophy } from 'lucide-react';
import { toast } from 'sonner';

/** Team colours, so the two buttons can never be mistaken for each other.
 *  Keyed by the feed's own abbreviation; anything unknown falls back to two
 *  clearly different colours by side. */
const TEAM_COLOURS = {
    MTL: '#AF1E2D', TOR: '#00205B', BUF: '#003087', BOS: '#FFB81C', OTT: '#C8102E',
    WSH: '#C8102E', WPG: '#041E42', STL: '#002F87', CHI: '#CF0A2C', DAL: '#006847',
    COL: '#6F263D', TBL: '#002868', NJD: '#CE1126', NYR: '#0038A8', NYI: '#00539B',
    PIT: '#FCB514', PHI: '#F74902', CAR: '#CE1126', FLA: '#C8102E', DET: '#CE1126',
    NSH: '#FFB81C', MIN: '#154734', SJS: '#006D75', LAK: '#111111', VGK: '#B4975A',
    EDM: '#FF4C00', CGY: '#D2001C', VAN: '#00205B', SEA: '#99D9D9', ANA: '#F47A38',
    UTA: '#71AFE5', CBJ: '#002654',
};
const fallback = (side) => (side === 'home' ? '#1D4ED8' : '#B45309');
export const colourFor = (team) => TEAM_COLOURS[(team.abbrev || '').toUpperCase()] || fallback(team.side);

export const WinnerPick = ({ cardId, block, deviceId, t, c, onPicked, onButtonTap,
                            startOpen = false, showPoints = false }) => {
    const [open, setOpen] = useState(startOpen);
    const [busy, setBusy] = useState('');
    // The screen can render before the state arrives, and the operator view
    // has no block at all — hooks still have to run, so read it safely.
    const [mine, setMine] = useState(block?.my_pick || null);

    if (!block?.enabled) return null;

    const mineTeam = (block.teams || []).find((x) => x.side === mine);

    const pick = async (side) => {
        if (block.locked) return;
        setBusy(side);
        try {
            const res = await fetch(
                `${(typeof window !== 'undefined' ? window.location.origin : '')}/api/goal-game/public/${cardId}/winner-pick`,
                { method: 'POST', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ side, device_id: deviceId || '' }) });
            const out = await res.json().catch(() => ({}));
            if (!res.ok) {
                toast.error(out.detail === 'sign_in_first'
                    ? t('goalGame.winnerPick.signIn')
                    : out.detail === 'game_started'
                        ? t('goalGame.winnerPick.locked')
                        : t('goalGame.winnerPick.failed'));
                return;
            }
            setMine(out.my_pick);
            toast.success(t('goalGame.winnerPick.saved'));
            onPicked?.(out);
        } catch (_e) { toast.error(t('goalGame.winnerPick.failed')); }
        finally { setBusy(''); }
    };

    if (!open) {
        return (
            <button type="button" onClick={() => { try { onButtonTap?.(); } catch (_e) { /* never blocks */ } setOpen(true); }}
                    data-testid="goal-winner-open"
                    className="w-full min-h-[44px] rounded-xl border border-white/25 py-3 text-sm font-semibold flex items-center justify-center gap-2"
                    style={{ color: c.text, backgroundColor: 'rgba(255,255,255,0.06)' }}>
                <Trophy size={15} />
                {mineTeam
                    ? t('goalGame.winnerPick.mine', { team: mineTeam.abbrev })
                    : t('goalGame.winnerPick.button')}
            </button>
        );
    }

    return (
        <div className="rounded-2xl p-3 space-y-2" data-testid="goal-winner-pick"
             style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: c.text }}>
            <p className="text-sm font-bold">{t('goalGame.winnerPick.prompt')}</p>
            {/* POINTS ARE THE OPTIONAL PROGRAMME. A venue that does not run
                Patriot Points never shows a points line in the play path. */}
            {showPoints && Number(block.points || 0) > 0 && (
                <p className="text-xs opacity-75" data-testid="goal-winner-worth">
                    {t('goalGame.winnerPick.worth', { points: Number(block.points).toLocaleString() })}
                </p>
            )}
            <div className="grid grid-cols-2 gap-2">
                {(block.teams || []).map((team) => (
                    <button key={team.side} type="button" disabled={!!busy || block.locked}
                            onClick={() => pick(team.side)}
                            data-testid={`goal-winner-${team.side}`}
                            className={`min-h-[56px] rounded-xl px-3 py-3 text-base font-bold text-white shadow-lg transition-transform ${
                                mine === team.side ? 'ring-4 ring-white/80 scale-[1.02]' : 'opacity-90'}`}
                            style={{ backgroundColor: colourFor(team) }}>
                        {busy === team.side
                            ? <Loader2 className="animate-spin mx-auto" size={18} />
                            : <>{team.abbrev}<span className="block text-[11px] font-semibold opacity-90">{team.name}</span></>}
                    </button>
                ))}
            </div>
            <p className="text-xs opacity-70" data-testid="goal-winner-note">
                {block.locked
                    ? t('goalGame.winnerPick.locked')
                    : mineTeam
                        ? `${t('goalGame.winnerPick.mine', { team: mineTeam.abbrev })} · ${t('goalGame.winnerPick.change')}`
                        : t('goalGame.winnerPick.change')}
            </p>
        </div>
    );
};
